import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Select, Space, Tooltip, Typography } from "antd";

import { optionsSearch } from "../../utils/search";
import { GRAY5, GRAY_PLACEHOLDER, WHITE } from "../../constant/colors";
import FloatLabel from "./FloatLabel";
import {
  DIAGNOSIS_FILTER_ISC_CODE,
  DIAGNOSIS_FILTER_NAME,
  FILTER_CLAIM_ASSIGNEE,
  FILTER_CLAIM_LAST_SAVED_BY,
  FILTER_ENROLLMENT_ASSIGNEE,
  FILTER_PATIENT_ASSIGNEE
} from "../../constant/filters";
import useConditionalValidation from "../Patient/ApplicationFormEditor/components/form/utils/useConditionalValidation";

const commonStyle = {
  width: "100%",
  border: `0px`,
  borderBottom: `1px solid ${GRAY5}`,
  padding: 0
};

const { Text } = Typography;
const formatTooltipContent = (id, tooltipContent) => {
  if (
    id === FILTER_PATIENT_ASSIGNEE ||
    id === FILTER_ENROLLMENT_ASSIGNEE ||
    id === FILTER_CLAIM_ASSIGNEE ||
    id === FILTER_CLAIM_LAST_SAVED_BY
  ) {
    return tooltipContent;
  }

  if (!tooltipContent || tooltipContent.length === 0) {
    return "";
  }

  return tooltipContent
    .map((item) => {
      if (id === DIAGNOSIS_FILTER_ISC_CODE || id === DIAGNOSIS_FILTER_NAME) {
        return item.value;
      }
      return item.text || item.children;
    })
    .join(" | ");
};

const MultiSelect = ({
  children,
  maxTagCount = 1,
  showAllTags = false,
  selectAll = false,
  clearAll = false,
  useCommonStyle = true,
  onClearAll,
  onSelectAll,
  style = {},
  id,
  bordered = false,
  tooltipContent,
  setDiagnosisSearchResults = () => {},
  setDiagnosisIcdsSearchResults = () => {},
  clearSearchValue = () => {},
  ...props
}) => {
  const { t } = useTranslation();
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [isHovering, setHovering] = useState(false);

  const formattedTooltipContent = formatTooltipContent(id, tooltipContent);
  const tagCountValue = showAllTags ? undefined : maxTagCount;

  // Prevent the default mousedown focus-shift: mousedown moves focus off the search input, whose
  // blur handler clears searchText, which un-gates and unmounts the bulk-action buttons before
  // their click fires — so the action would be lost. Keeping focus on the input preserves them.
  const preventFocusShift = (e) => e.preventDefault();

  return (
    <div id={id}>
      <Tooltip
        open={formattedTooltipContent && !isDropdownOpen && isHovering}
        title={<Text>{formattedTooltipContent}</Text>}
        color={WHITE}
        placement="right"
        overlayStyle={{
          maxWidth: "calc(100% - 600px)",
          whiteSpace: "normal",
          wordWrap: "break-word"
        }}
      >
        <Select
          {...props}
          mode="multiple"
          bordered={bordered}
          showArrow={true}
          allowClear={true}
          onClear={onClearAll}
          maxTagCount={tagCountValue}
          dropdownStyle={selectAll && clearAll ? { borderRadius: 8 } : undefined}
          getPopupContainer={(trigger) => trigger.parentNode}
          onDropdownVisibleChange={(open) => {
            if (id === DIAGNOSIS_FILTER_ISC_CODE) {
              setDiagnosisIcdsSearchResults([]);
            }
            if (id === DIAGNOSIS_FILTER_NAME) {
              !open && setDiagnosisSearchResults([]);
            }
            if (!open) clearSearchValue();
            setDropdownOpen(open);
          }}
          onMouseEnter={() => setHovering(true)}
          onMouseLeave={() => setHovering(false)}
          style={{
            ...(useCommonStyle && commonStyle),
            ...style,
            padding: props.label ? "10px 0 0 0" : ""
          }}
          dropdownRender={(menu) => (
            <div>
              {selectAll && clearAll && (
                <>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "nowrap",
                      padding: "8px 8px 12px",
                      gap: 8,
                      borderBottom: "1px solid #E4E7EC"
                    }}
                  >
                    <style>
                      {`
                        .multi-select-action-btn.ant-btn {
                          height: 32px;
                          border-radius: 8px;
                          border-color: #D0D5DD;
                          font-weight: 600;
                          padding: 4px 8px;
                          color: black;
                        }
                        .multi-select-action-btn.ant-btn:hover {
                          color: black;
                          border-color: #98A2B3;
                        }
                        .multi-select-action-btn.ant-btn:active {
                          color: black;
                          border-color: #E4E7EC;
                        }
                        .multi-select-action-btn.ant-btn:focus {
                          color: black;
                          border-color: #D0D5DD;
                        }
                      `}
                    </style>
                    <Button
                      block
                      className="multi-select-action-btn"
                      onMouseDown={preventFocusShift}
                      onClick={onSelectAll}
                    >
                      {t("select_all")}
                    </Button>
                    <Button
                      className="multi-select-action-btn"
                      onMouseDown={preventFocusShift}
                      onClick={onClearAll}
                      style={{ flexShrink: 0 }}
                    >
                      {t("clear")}
                    </Button>
                  </div>
                </>
              )}
              <div style={{ paddingTop: 4 }}>{menu}</div>
            </div>
          )}
        >
          {children}
        </Select>
      </Tooltip>
    </div>
  );
};

const SingleSelect = ({
  name,
  children,
  filterOption = optionsSearch,
  required = false,
  showSearch = true,
  bordered = false,
  useCommonStyle = true,
  style,
  shouldValidateFields = false,
  ...props
}) => {
  useConditionalValidation(name, shouldValidateFields);
  return (
    <FloatLabel
      label={
        <Space size={4}>
          {required && <Text style={{ color: GRAY_PLACEHOLDER }}>*</Text>}
          <Text style={{ color: GRAY_PLACEHOLDER }}>{props.label}</Text>
        </Space>
      }
      name={props.label}
      value={props.value}
    >
      <Select
        bordered={bordered}
        filterOption={filterOption}
        showSearch={showSearch}
        style={{
          ...(useCommonStyle && commonStyle),
          padding: props.label ? "6px 0 0 0" : "",
          ...style
        }}
        placeholder={props.placeholder && (required ? `* ${props.placeholder}` : props.placeholder)}
        getPopupContainer={(trigger) => trigger.parentNode}
        {...props}
      >
        {children}
      </Select>
    </FloatLabel>
  );
};

export { SingleSelect, MultiSelect };
